import { ADDRESS_DEFAULT_COLUMNS, CP_ADDRESS_TABLE_ALIAS } from 'src/app/AddressBook/addressBook.const';
import { INVOICE_DEFAULT_COLUMNS, INVOICE_TABLE_ALIAS, POD_DEFAULT_COLUMNS, POD_TABLE_ALIAS } from 'src/app/DocumentHub/documentHub.const';
import { CP_QUOTE_DEFAULT_COLUMNS, CP_QUOTE_TABLE_ALIAS } from 'src/app/MyQuote/myQuote.const';
import { mergeColumnSettings } from 'src/app/MyQuote/myQuote.helpers';
import { CP_ORDER_DEFAULT_COLUMNS, CP_ORDER_TABLE_ALIAS } from 'src/app/Orders/order.const';
import { CP_ROUTE_OVERVIEW_TABLE_ALIAS, ROUTE_OVERVIEW_DEFAULT_COLUMNS } from 'src/app/Orders/OrderView/LegOverview/legOverview.const';
import { TRACK_DEFAULT_COLUMNS, TRACK_TABLE_ALIAS } from 'src/app/Track/track.const';
import { getInitData } from 'src/services/QuoteService';

import { applyShipperConsigneeData, fetchShippers, setIsShipperLoading } from './address.slice';
import {
    applyCommoditiesData,
    applyEquipmentData,
    applyHandlingUnitsData,
    applyServicesData,
    applyTeamMemberListData,
    fetchAllTeamMemberListOptions,
    fetchCommodities,
    fetchEquipmentList,
    fetchHandlingUnits,
    fetchServices,
    setAddressColumns,
    setInvoiceColumns,
    setIsCommodityLoading,
    setIsHandlingUnitLoading,
    setIsInitLoaded,
    setIsServiceLoading,
    setIsTeamLoading,
    setIsVehicleAttrLoading,
    setIsVehicleTypeLoading,
    setOrderColumns,
    setOrderRouteOverviewColumns,
    setPodColumns,
    setQuoteColumns,
    setTrackColumns,
} from './basic.slice';

/**
 * Binds the column settings slice of the init payload.
 * `columnSettings` is a map keyed by table alias: { cp_quote: [...], cp_order: [...], ... }.
 */
const applyColumnSettings = (dispatch: any, columnSettings: Record<string, any[]>) => {
    const savedQuote = columnSettings[CP_QUOTE_TABLE_ALIAS] ?? [];
    dispatch(setQuoteColumns(mergeColumnSettings(savedQuote, CP_QUOTE_DEFAULT_COLUMNS)));

    const savedOrder = columnSettings[CP_ORDER_TABLE_ALIAS] ?? [];
    dispatch(setOrderColumns(mergeColumnSettings(savedOrder, CP_ORDER_DEFAULT_COLUMNS)));

    const savedInvoice = columnSettings[INVOICE_TABLE_ALIAS] ?? [];
    // daysPending and fileName are gone from INVOICE_DEFAULT_COLUMNS, but mergeColumnSettings
    // preserves saved columns even when they're gone from the defaults — filter them here
    // too so users with saved column settings also stop seeing them.
    const REMOVED_INVOICE_COLUMNS = ['daysPending', 'fileName'];
    dispatch(setInvoiceColumns(mergeColumnSettings(savedInvoice, INVOICE_DEFAULT_COLUMNS).filter((col: any) => !REMOVED_INVOICE_COLUMNS.includes(col.id))));

    const savedPod = columnSettings[POD_TABLE_ALIAS] ?? [];
    dispatch(setPodColumns(mergeColumnSettings(savedPod, POD_DEFAULT_COLUMNS)));

    const savedAddress = columnSettings[CP_ADDRESS_TABLE_ALIAS] ?? [];
    dispatch(setAddressColumns(mergeColumnSettings(savedAddress, ADDRESS_DEFAULT_COLUMNS)));

    const savedTrack = columnSettings[TRACK_TABLE_ALIAS] ?? [];
    dispatch(setTrackColumns(mergeColumnSettings(savedTrack, TRACK_DEFAULT_COLUMNS)));

    // Order detail → Route Overview. Only the serialisable settings go into redux; the JSX
    // preview renderers (getValue) are re-attached from the defaults by useRouteOverviewColumns.
    const savedRouteOverview = columnSettings[CP_ROUTE_OVERVIEW_TABLE_ALIAS] ?? [];
    dispatch(
        setOrderRouteOverviewColumns(mergeColumnSettings(savedRouteOverview, ROUTE_OVERVIEW_DEFAULT_COLUMNS).map(({ id, name, visible, displayOrder }: any) => ({ id, name, visible, displayOrder })))
    );
};

const setBootLoadingFlags = (dispatch: any, isLoading: boolean) => {
    dispatch(setIsServiceLoading(isLoading));
    dispatch(setIsVehicleAttrLoading(isLoading));
    dispatch(setIsVehicleTypeLoading(isLoading));
    dispatch(setIsHandlingUnitLoading(isLoading));
    dispatch(setIsCommodityLoading(isLoading));
    dispatch(setIsShipperLoading(isLoading));
    dispatch(setIsTeamLoading(isLoading));
};

/**
 * The reference-data sections of the init payload.
 *
 * `isPresent` decides whether this deploy of the backend actually returned the section. The
 * frontend and backend deploy separately, so the frontend MUST tolerate a backend that predates
 * the aggregated init response (it returns `data.columnSettings` only). Anything missing — or
 * reported as failed in `errors` — is fetched once, here, at boot. This is the single recovery
 * point; no page or modal fetches this data for itself.
 *
 * Once the matching backend is deployed, `isPresent` is true for every section and ZERO fallback
 * requests are made — the app loads on one call.
 */
const INIT_SECTIONS = [
    {
        key: 'services',
        isPresent: (data: any) => Array.isArray(data?.services),
        apply: (dispatch: any, data: any) => applyServicesData(dispatch, data.services ?? []),
        fallback: fetchServices,
    },
    {
        key: 'handlingUnits',
        isPresent: (data: any) => Array.isArray(data?.handlingUnits),
        apply: (dispatch: any, data: any) => applyHandlingUnitsData(dispatch, data.handlingUnits ?? []),
        fallback: fetchHandlingUnits,
    },
    {
        key: 'equipment',
        isPresent: (data: any) => !!data?.equipment && typeof data.equipment === 'object',
        apply: (dispatch: any, data: any) => applyEquipmentData(dispatch, data.equipment),
        fallback: fetchEquipmentList,
    },
    {
        key: 'commodities',
        isPresent: (data: any) => Array.isArray(data?.commodities),
        apply: (dispatch: any, data: any) => applyCommoditiesData(dispatch, data.commodities ?? []),
        fallback: fetchCommodities,
    },
    {
        key: 'shipperConsignee',
        isPresent: (data: any) => !!data?.shipperConsignee && Array.isArray(data.shipperConsignee.list),
        apply: (dispatch: any, data: any) => applyShipperConsigneeData(dispatch, data.shipperConsignee.list, data.shipperConsignee.commonCarrierRequiredFields),
        fallback: fetchShippers,
    },
    {
        // Team-member filter options on the quote and order listings. Those pages each used to
        // fetch /organization/members on mount; they now read this from redux. The Organization
        // page still calls the endpoint directly — it needs its own paginated, searchable,
        // role-filtered query — and keeps this cached copy in step via upsertTeamMembers /
        // removeTeamMember rather than making anyone re-fetch.
        key: 'members',
        isPresent: (data: any) => Array.isArray(data?.members),
        apply: (dispatch: any, data: any) => applyTeamMemberListData(dispatch, data.members ?? []),
        fallback: fetchAllTeamMemberListOptions,
    },
];

/**
 * Binds every section of an init response into Redux.
 * Returns the sections the response did not usably provide, so the caller can recover them.
 */
const applyInitPayload = (dispatch: any, res: any): string[] => {
    const data = res?.data ?? {};
    const errors: Record<string, string> = res?.errors ?? {};
    const missing: string[] = [];

    // Column settings have no standalone fallback — a miss just leaves the default column order,
    // which is exactly what the previous implementation did on failure.
    if (!errors.columnSettings && data.columnSettings) {
        applyColumnSettings(dispatch, data.columnSettings);
    }

    INIT_SECTIONS.forEach((section) => {
        if (!errors[section.key] && section.isPresent(data)) {
            section.apply(dispatch, data);
        } else {
            missing.push(section.key);
        }
    });

    if (Object.keys(errors).length) {
        console.error('Init: some sections failed server-side:', errors);
    }

    return missing;
};

/**
 * THE one call the portal makes on landing.
 *
 * A single GET /common/init returns every piece of reference data the app needs — column
 * settings, services, handling units, equipment types, commodities, shippers/consignees and
 * organization members — and binds all of it into Redux. Every page and modal reads that Redux state; none of them fetch
 * this data themselves (see useAddressDetails, which is now a pure reader).
 *
 * On a hard failure the call is retried ONCE against the same endpoint. Only if init still cannot
 * supply a section — because it errored, or because the deployed backend predates the aggregated
 * response — is that section fetched from its own endpoint, once, here at boot. Pages and modals
 * never do this themselves; the per-page fan-out is exactly what this replaces.
 */
export const fetchInitData =
    (): any =>
    async (dispatch: any): Promise<void> => {
        setBootLoadingFlags(dispatch, true);

        try {
            // Interceptor unwraps response.data for 200s, so res IS the JSON body
            let res: any;

            try {
                res = await getInitData();
            } catch (firstError) {
                console.error('Error fetching init data, retrying once:', firstError);
                res = await getInitData();
            }

            const missing = applyInitPayload(dispatch, res);

            // Release the flags before any recovery runs — each fallback thunk owns its own flag.
            setBootLoadingFlags(dispatch, false);

            if (missing.length) {
                console.warn(`Init did not provide [${missing.join(', ')}] — fetching them once at boot. Is the backend deployed?`);

                missing.forEach((key) => {
                    const section = INIT_SECTIONS.find((s) => s.key === key);

                    if (section) dispatch(section.fallback() as any);
                });
            }
        } catch (error) {
            // Both attempts failed outright. Recover everything once so the app is still usable.
            console.error('Error fetching init data, recovering sections individually:', error);
            setBootLoadingFlags(dispatch, false);

            INIT_SECTIONS.forEach((section) => dispatch(section.fallback() as any));
        } finally {
            dispatch(setIsInitLoaded(true));
        }
    };
